import React from "react";
import {
	Chart as ChartJS,
	RadialLinearScale,
	PointElement,
	LineElement,
	Filler,
	Tooltip,
	Legend,
	Title,
} from "chart.js";
import { Radar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import { IconButton, Button, Typography } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import { DataGrid } from "@mui/x-data-grid";
import CircularProgress from "@mui/material/CircularProgress";
import { CustomPagination, CustomNoRowsOverlay } from "../../../SecondaryFunctions";
import InfoIcon from "@mui/icons-material/Info";


const CenterMinLabel = {
	id: "centerMinLabel",
	afterDraw(chart, _args, pluginOpts) {
		const scale = chart.scales?.r;
		if (!scale) return;

		// Build the label text
		const minValue = scale.min;
		let label;
		if (pluginOpts && typeof pluginOpts.formatter === "function") {
			label = pluginOpts.formatter(minValue, scale);
		} else {
			label = String(minValue);
		}

		const ctx = chart.ctx;

		// Derive a canvas font string from tick options and Chart defaults
		const tickOpts = scale.options.ticks || {};
		const globalFont = ChartJS.defaults.font || {};
		const f = tickOpts.font || {};
		const size = f.size ?? globalFont.size ?? 12;
		const family = f.family ?? globalFont.family ?? "sans-serif";
		const style = f.style ?? globalFont.style ?? "";
		const weight = f.weight ?? globalFont.weight ?? "";
		const fontString = `${style ? style + " " : ""}${weight ? weight + " " : ""}${size}px ${family}`;

		const color = tickOpts.color || "#666";

		ctx.save();
		ctx.font = fontString;
		ctx.fillStyle = color;
		ctx.textAlign = "center";
		ctx.textBaseline = "middle";

		// Draw at the exact chart center
		const x = scale.xCenter ?? (chart.chartArea.left + chart.chartArea.right) / 2;
		const y = scale.yCenter ?? (chart.chartArea.top + chart.chartArea.bottom) / 2;
		ctx.fillText(label, x, y);
		ctx.restore();
	},
};

ChartJS.register(
	RadialLinearScale,
	PointElement,
	LineElement,
	Filler,
	Tooltip,
	Legend,
	Title,
	ChartDataLabels,
	CenterMinLabel,
);

const columns = [
	{
		field: "term",
		headerName: "Term",
		flex: 3, // 2 / 10 = 20%
		// minWidth: 140,
		align: "center",
		headerAlign: "center",
		renderCell: (params) => (
			<div className="flex items-center justify-center h-full">{params.value}</div>
		),
	},
	{
		field: "course",
		headerName: "Course",
		flex: 7, // 4 / 10 = 40%
		// minWidth: 280,
		align: "center",
		headerAlign: "center",
		renderCell: (params) => (
			<div className="flex items-center justify-center h-full">{params.value}</div>
		),
	},
];

// This function creates a summary data object with specified properties, excluding the question.
function createSummaryData(category, rsp1, rsp2, rsp3, rsp4, rsp5, backgroundColor) {
	return { category, rsp1, rsp2, rsp3, rsp4, rsp5, backgroundColor };
}

// This function summarizes the provided data by category, aggregating response values.
function summarizeData(data) {
	const summary = data.reduce((acc, row) => {
		if (row.category in acc) {
			// Aggregate response values for existing category.
			acc[row.category][0] += Number(row.rsp1);
			acc[row.category][1] += Number(row.rsp2);
			acc[row.category][2] += Number(row.rsp3);
			acc[row.category][3] += Number(row.rsp4);
			acc[row.category][4] += Number(row.rsp5);
		} else {
			// Create a new category entry with aggregated response values.
			acc[row.category] = [
				Number(row.rsp1),
				Number(row.rsp2),
				Number(row.rsp3),
				Number(row.rsp4),
				Number(row.rsp5),
				row.backgroundColor,
			];
		}
		return acc;
	}, {});

	// Convert the summarized data into an array of summary objects.
	const output = Object.keys(summary).map((key) => {
		const [rsp1, rsp2, rsp3, rsp4, rsp5, backgroundColor] = summary[key];
		return createSummaryData(key, rsp1, rsp2, rsp3, rsp4, rsp5, backgroundColor);
	});

	return output;
}

function UAspotRadarChart({
	courseINFO,
	spotData,
	radarData,
	setRadarData,
	comparisonData,
	setComparisonData,
	comparisonLoading,
	setComparisonLoading,
	additionalData,
	setAdditionalData,
	tooManyComparisons,
	setTooManyComparisons,
	radarChartMin,
	setRadarChartMin,
	radarChartScale,
	setRadarChartScale,
	infoWindow,
	setInfoWindow,
}) {
	const openInfoWindow = () => {
		setInfoWindow(true);
	};

	const closeInfoWindow = () => {
		setInfoWindow(false);
	};

	async function fetchComparisonData() {
		const masterRecord = `${courseINFO[5]}_${courseINFO[6]}`;
		// Determine which employee IDs to fetch based on access level
		let empids = sessionStorage.getItem("id");

		const url = process.env.REACT_APP_BACKEND_HOST + "/spot/radar_data/";
		const response = await fetch(url, {
			method: "POST",
			headers: {
				Authorization: `Token ${sessionStorage.token}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ empids }),
		});

		const json = await response.json();
		const apiData = {};

		for (const item of json) {
			const arrayId = `${item.REQNUM}_${item.CLSNUM}`;

			if (arrayId === masterRecord) continue;

			const numericalData = {
				category: item.CAT_NAME,
				rsp1: item.RSP1,
				rsp2: item.RSP2,
				rsp3: item.RSP3,
				rsp4: item.RSP4,
				rsp5: item.RSP5,
			};

			if (!apiData[arrayId]) {
				apiData[arrayId] = {
					term: item.DESC_TERM.trim(),
					course: item.CTITLE.trim(),
					questions: [numericalData],
				};
			} else {
				apiData[arrayId].questions.push(numericalData);
			}
		}

		// Convert object to array for DataGrid
		const formattedData = Object.entries(apiData).map(([key, entry]) => ({
			id: key,
			term: entry.term,
			course: entry.course,
			instructor: entry.instructor,
			questions: summarizeData(entry.questions),
		}));

		return formattedData;
	}

	const calculateStats = (responseArray) => {
		return responseArray.map((item) => {
			const frequencies = [1, 2, 3, 4, 5].map((i) => item[`rsp${i}`] || 0);
			const total = frequencies.reduce((a, b) => a + b, 0);

			if (total === 0) {
				return {
					category: item.category,
					median: null,
				};
			}

			const medianPos = total / 2;
			let cumulative = 0;
			let median = null;

			for (let i = 0; i < frequencies.length; i++) {
				const f = frequencies[i];
				cumulative += f;

				if (cumulative >= medianPos) {
					const cf = cumulative - f; // cumulative before this category
					const L = i + 1 - 0.5; // lower boundary: 0.5, 1.5, 2.5, 3.5, 4.5
					const w = 1; // bin width

					median = L + ((medianPos - cf) / f) * w;
					break;
				}
			}

			// Safety clamp (shouldn’t really fire, but harmless)
			median = Math.min(median, 5);
			median = parseFloat(median.toFixed(1)); // or 1 decimal if you prefer

			return {
				category: item.category,
				median,
			};
		});
	};

	const updateRadarScale = (radarData) => {
		const medians = radarData.datasets.flatMap((dataset) => dataset.data);
		const minMedian = Math.min(...medians);
		if (minMedian >= 4) {
			setRadarChartMin(3);
			setRadarChartScale(1);
		} else {
			setRadarChartMin(1);
			setRadarChartScale(1);
		}
	};

	const setDefaultRadarData = (courseINFO, spotData) => {
		const stats = calculateStats(spotData);
		const chartData = {
			labels: stats.map((stat) => stat.category),
			datasets: [
				{
					// label: `${courseINFO[2]} - ${courseINFO[0]} - ${courseINFO[1]}`,
					label: `${courseINFO[2]} - ${courseINFO[0]}`,
					data: stats.map((stat) => stat.median),
					// backgroundColor: 'rgba(39, 93, 56, 0.2)',
					backgroundColor: "rgba(39, 93, 56, 0.1)",
					borderColor: "rgba(39, 93, 56, 1)",
					borderWidth: 2,
					pointBackgroundColor: "rgba(39, 93, 56, 1)",
				},
			],
		};
		updateRadarScale(chartData);
		setRadarData(chartData);
	};

	function includeComparisonData(selected) {
		const colours = [
			{
				// backgroundColor: 'rgba(255, 182, 0, 0.2)',
				backgroundColor: "rgba(255, 182, 0, 0.1)",
				borderColor: "rgba(255, 182, 0, 1)",
				pointBackgroundColor: "rgba(255, 182, 0, 1)",
			},
			{
				// backgroundColor: 'rgba(25, 25, 25, 0.2)',
				// backgroundColor: 'rgba(25, 25, 25, 0.1)',
				// borderColor: 'rgba(25, 25, 25, 1)',
				// pointBackgroundColor: 'rgba(25, 25, 25, 1)',
				backgroundColor: "rgba(230, 23, 23, 0.1)",
				borderColor: "rgba(230, 23, 23, 1)",
				pointBackgroundColor: "rgba(230, 23, 23, 1)",
			},
		];

		// The main course dataset is always the first one
		const baseDataset = radarData.datasets[0];

		const updatedRadarData = {
			labels: [...radarData.labels],
			datasets: [baseDataset], // start with only the primary course
		};

		if (selected.length > 0 && !tooManyComparisons) {
			selected.forEach((targetId, index) => {
				const data = comparisonData.find((item) => item.id === targetId);
				if (!data) return;

				// const courseName = `${data.term} - ${data.course} - ${data.instructor}`;
				const courseName = `${data.term} - ${data.course}`;

				// Avoid duplicates (shouldn't happen now since we start fresh)
				if (updatedRadarData.datasets.some((ds) => ds.label === courseName)) return;

				const numerical = calculateStats(data.questions);

				updatedRadarData.datasets.push({
					label: courseName,
					data: numerical.map((stat) => stat.median),
					...colours[index],
					borderWidth: 2,
				});
			});
		}
		updateRadarScale(updatedRadarData);
		setRadarData(updatedRadarData);
	}

	function handleSelection(newSelection) {
		let items;
		if (Array.isArray(newSelection)) {
			items = newSelection;
		} else if (newSelection?.type === "exclude") {
			if (newSelection?.ids.size > 0) {
				const excludedIds = new Set(newSelection.ids);
				items = new Set(
					comparisonData.filter((row) => !excludedIds.has(row.id)).map((row) => row.id),
				);
			} else {
				items = new Set(comparisonData.map((row) => row.id));
			}
		} else if (newSelection?.type === "include" && newSelection?.ids) {
			items = newSelection.ids;
		} else {
			items = [];
		}

		//convert idArray to an array of numbers FROM set
		if (items instanceof Set) {
			items = Array.from(items).map((id) => id);
		}

		setAdditionalData(items);
		includeComparisonData(items);
	}

	const selectionModel = React.useMemo(
		() => ({ type: "include", ids: new Set(additionalData) }),
		[additionalData]
	);

	const isRowSelectable = (params) => {
		// Allow selection if not already selected or if less than 3 rows are selected
		if (additionalData.includes(params.id)) {
			return true;
		}
		return additionalData.length < 2;
	};

	const resetRadarData = () => {
		setAdditionalData([]);
		setDefaultRadarData(courseINFO, spotData);
	};

	// const radarOptions = {
	//   responsive: true,
	//   maintainAspectRatio: false,
	//   plugins: {
	//     legend: {
	//       position: 'bottom', labels: { padding: 10, font: {size: 12 }}
	//     },
	//     tooltip: {
	//       bodyFont: {size: 14}, titleFont: {size: 14},
	//       callbacks: {
	//         label: function(context) {
	//           const value = context.raw;
	//           return `${context.dataset.label}: ${value}`;
	//         },
	//         afterLabel: function(context) {
	//           const value = context.raw;
	//           if (value <= 3) {
	//             return `Click here for best practices on ${context.label}`;
	//           }
	//           return null; // nothing extra if condition not met
	//         }
	//       }
	//   },
	//     datalabels: datalabelsConfig,
	//      centerMinLabel: {
	//       formatter: (minValue, scale) => {
	//         if (scale.min === 3) {return "1–3"}
	//         else{return String(minValue)}
	//       }
	//     }
	//   },

	//   scales: {
	//     r: {
	//       min: radarChartMin,
	//       max: 5,
	//       ticks: {
	//         stepSize: radarChartScale,
	//         color: 'black',
	//         font: { size: 14, weight: 'bold' },
	//         callback: (value) => {
	//           if (radarChartMin === 3) {
	//             if (value === 4) return "4";
	//             if (value === 5) return "5";
	//             return "";
	//           } else {
	//             // default case: show 1–5
	//             return String(value);
	//           }
	//         },
	//       },
	//       pointLabels: { color: 'black', font: { size: 14 } },
	//       grid: { color: '#aaaaaa' },
	//       angleLines: { color: '#aaaaaa' }
	//     }
	//   }
	//   // scales: {
	//   //   r: {
	//   //     min: radarChartMin ,max: 5,ticks: {stepSize: 1,font: {size: 12}},
	//   //     pointLabels: {color: 'black',font: {size: 14}},
	//   //     grid: {color: '#aaaaaa'},
	//   //     angleLines: {color: '#aaaaaa'}
	//   //   }
	//   // }
	// };

	function makeExternalTooltip(tooltipId = "radar-tip") {
		return function externalTooltipHandler(context) {
			const { chart, tooltip } = context;
			let tip = document.getElementById(tooltipId);

			// Create the tooltip element on first run
			if (!tip) {
				tip = document.createElement("div");
				tip.id = tooltipId;
				tip.style.position = "absolute";
				tip.style.pointerEvents = "auto"; // <-- allow clicking
				tip.style.transform = "translate(-50%, -100%)";
				tip.style.background = "rgba(25,25,25,0.85)";
				tip.style.color = "#fff";
				tip.style.padding = "8px 10px";
				tip.style.borderRadius = "8px";
				tip.style.boxShadow = "0 6px 18px rgba(0,0,0,0.25)";
				tip.style.font = "12px sans-serif";
				tip.style.zIndex = 9999;
				tip.style.whiteSpace = "nowrap";
				tip.style.userSelect = "text";
				tip.style.maxWidth = "320px";
				tip.style.textAlign = "left";

				// Keep visible while hovering the tooltip itself
				tip.addEventListener("mouseenter", () => {
					chart.$extHover = true;
					clearTimeout(chart.$extHideTimer);
				});
				tip.addEventListener("mouseleave", () => {
					chart.$extHover = false;
					chart.$extHideTimer = setTimeout(() => {
						if (!chart.$extHover) {
							tip.style.opacity = "0";
							tip.style.pointerEvents = "none";
						}
					}, 1500);
				});

				document.body.appendChild(tip);
			}

			// Hide if no opacity (no active point) and we’re not hovering the tooltip
			if (tooltip.opacity === 0 && !chart.$extHover) {
				clearTimeout(chart.$extHideTimer);
				chart.$extHideTimer = setTimeout(() => {
					if (!chart.$extHover) {
						tip.style.opacity = "0";
						tip.style.pointerEvents = "none";
					}
				}, 1000); // <-- delay before hiding
				return;
			}

			// Grab the datapoint
			const dp = tooltip.dataPoints?.[0];
			if (!dp) return;

			const label = dp.label;
			const value = dp.formattedValue ?? dp.raw;
			const color = dp.dataset?.borderColor || "rgba(39, 93, 56, 1)";

			// const slug = String(label)
			//   .toLowerCase()
			//   .replace(/\s+/g, '-')
			//   .replace(/[^a-z0-9-]/g, '');
			// const url = 'https://www.ualberta.ca/en/centre-for-teaching-and-learning/resources/multifaceted-evaluation-of-teaching/student-perspectives-of-teaching.html';
			const url =
				"https://docs.google.com/forms/d/e/1FAIpQLSei2yZUSSrDQ41IrvBAcZ9o3ZYeQtgg60340NTmoOvNMJmM5g/viewform";

			tip.innerHTML = `
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
          <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};"></span>
          <strong>${label}</strong>
        </div>
        <div style="margin-bottom:2px;">${dp.dataset.label}</div>
        <div style="margin-bottom:6px;">Score: <strong>${value}</strong></div>
        
        <div>
          <div>For support with this domain, please contact</div>
          <div>the Centre for Teaching and Learning (CTL)</div>
          <a href="${url}" target="_blank" rel="noopener noreferrer" 
               style="color:#ffd24d;text-decoration:underline;">
                Request for CTL Consultation
          </a>
        </div>
        
  
      `;
			//  ${
			//     Number(dp.raw) <= 3
			//     ? `<a href="${url}" target="_blank" rel="noopener noreferrer"
			//          style="color:#ffd24d;text-decoration:underline;">
			//          Click for best practices on ${label}</a>`
			//     : `<span style="opacity:.85;">✅ Excellent Performance</span>`
			//   }

			// Position it near the caret
			const { canvas } = chart;
			const rect = canvas.getBoundingClientRect();
			const x = rect.left + window.scrollX + tooltip.caretX;
			const y = rect.top + window.scrollY + tooltip.caretY - 10;

			tip.style.left = `${x}px`;
			tip.style.top = `${y}px`;
			tip.style.opacity = "1";
			tip.style.pointerEvents = "auto";
			clearTimeout(chart.$extHideTimer); // cancel hide timer if re-showing
		};
	}

	const radarOptions = {
		responsive: true,
		maintainAspectRatio: false,
		interaction: { mode: "nearest", intersect: true },
		events: ["mousemove", "mouseout", "click", "touchstart", "touchmove"], // default + safe for hover/click
		elements: {
			point: {
				radius: 3,
				hoverRadius: 6,
				borderWidth: 3,
				hitRadius: 10,
			},
		},
		plugins: {
			legend: {
				position: "bottom",
				labels: { padding: 10, font: { size: 12 } },
			},
			title: {
				display: true,
				text: "SPOT Domain Analysis (Interpolated Medians)", // <- your chart title
				font: { size: 20, weight: "normal" },
				color: "black",
				padding: { bottom: 20 },
			},
			tooltip: {
				enabled: false, // IMPORTANT: turn off canvas tooltip
				external: makeExternalTooltip("radar-tip"),
				// (Callbacks no longer needed for text; we build HTML in external)
			},
			// datalabels: datalabelsConfig,
			datalabels: {
				// Show only when exactly 1 dataset (avoid issues when 0 or >1)
				display: (ctx) => ctx?.chart?.data?.datasets?.length === 1,

				// Usual styling
				color: "black",
				font: { size: 14, weight: "bold" },
				anchor: "end",
				align: "start",
				offset: 8,

				// Guard against null/undefined values
				formatter: (value) =>
					value == null || Number.isNaN(value) ? "" : Number(value).toFixed(1),
			},
			centerMinLabel: {
				formatter: (minValue, scale) => (scale.min === 3 ? "1–3" : String(minValue)),
			},
		},
		scales: {
			r: {
				min: radarChartMin,
				max: 5,
				ticks: {
					stepSize: radarChartScale,
					color: "black",
					font: { size: 14, weight: "bold" },
					callback: (value) => {
						if (radarChartMin === 3) {
							if (value === 4) return "4";
							if (value === 5) return "5";
							return "";
						}
						return String(value);
					},
				},
				pointLabels: { color: "black", font: { size: 14 } },
				grid: { color: "#aaaaaa" },
				angleLines: { color: "#aaaaaa" },
			},
		},
		// Optional: make cursor a pointer when near a point
		onHover: (e, elements) => {
			const canvas = e.native?.target;
			if (canvas) canvas.style.cursor = elements?.length ? "pointer" : "default";
		},
	};

	if (radarData === null) {
		setDefaultRadarData(courseINFO, spotData);
		fetchComparisonData().then((data) => {
			setComparisonData(data);
			setComparisonLoading(false);
		});
	} else {
		return (
			<div>
				<Dialog fullWidth={true} maxWidth={"lg"} open={infoWindow} onClose={closeInfoWindow}>
					<DialogTitle>How Are These Numbers Calculated?</DialogTitle>
					<DialogContent>
						<DialogContentText>
							For each SPOT domain, we sum the number of responses for each response value on the
							likert scale (ranging from Strongly Disagree to Strongly Agree). These totals
							represent how many respondents selected each value from 1 to 5. Using those summed
							values, we calculate the interpolated median to better understand the central tendency
							of the responses for that domain.
						</DialogContentText>
						<br />
						<DialogContentText>
							To calculate the interpolated median, we first determine the total number of responses
							and identify the median position, which is simply half of that total. We then step
							through the cumulative frequencies to find the response value in which the median
							falls. Rather than simply selecting that response value as the median, we apply
							interpolation to estimate a more precise, decimal-valued result within that range.
						</DialogContentText>
						<br />
						{/* Formula */}
						<DialogContentText sx={{textAlign: "center"}}>
							Median = <em>L</em> + ((<em>N/2</em> − <em>cf</em>) / <em>f</em> ) × <em>w</em>
						</DialogContentText>

						<br />

						{/* Definitions */}
						<DialogContentText>
							Where:
						</DialogContentText>
						<DialogContentText sx={{paddingLeft: 5 }}>
							<li><em>L</em> = lower boundary of the median category</li>
							<li><em>N</em> = total responses</li>
							<li><em>cf</em> = cumulative frequency before the median class</li>
							<li><em>f</em> = frequency of the median class</li>
							<li><em>w</em> = class width (1 for Likert)</li>
						</DialogContentText>

					</DialogContent>
					<DialogActions>
						<Button onClick={closeInfoWindow}>Close</Button>
					</DialogActions>
				</Dialog>

				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "flex-start",
					}}
				>
					<IconButton
						edge="start"
						onClick={openInfoWindow}
						sx={{
							color: "#275d38", // base color
							"&:hover": {
								color: "#1e472c", // darker shade on hover
								backgroundColor: "transparent", // optional: keep background clear
							},
						}}
					>
						<InfoIcon />
					</IconButton>

					{/* Radar Chart */}
					<div
						style={{
							// borderWidth: '2px',
							// borderStyle: 'dashed',
							// borderColor: 'gray',
							// borderRadius: '8px',
							// display: 'flex',
							// justifyContent: 'center',  // center horizontally
							// alignItems: 'flex-start',  // align to top
							height: "54vh", // or any desired height
							width: "65%",
						}}
					>
						<Radar data={radarData} options={radarOptions} />
					</div>

					<div
						style={{
							// borderWidth: '2px',
							// borderStyle: 'dashed',
							// borderColor: 'gray',
							// borderRadius: '8px',
							height: "55vh",
							width: "35%",
							maxWidth: "700px",
						}}
					>
						<DataGrid
							getRowHeight={() => "auto"}
							disableColumnSelector
							columns={columns}
							rows={comparisonData}
							checkboxSelection
							hideFooterPagination
							pageSize={100}
							isRowSelectable={isRowSelectable}
							rowsPerPageOptions={[100]}
							// selectionModel={additionalData}
							// onRowSelectionModelChange={handleSelection}
							rowSelectionModel={selectionModel}
							onRowSelectionModelChange={(newSelection) => {
								// newSelection is { type, ids: Set }
								const idsArray =
								newSelection?.type === "exclude"
									? comparisonData
										.filter((row) => !newSelection.ids.has(row.id))
										.map((row) => row.id)
									: Array.from(newSelection?.ids ?? []);

								setAdditionalData(idsArray);
								includeComparisonData(idsArray);
							}}
							density={"compact"}
							slots={{
								Pagination: CustomPagination,
								LoadingOverlay: CircularProgress,
								NoRowsOverlay: CustomNoRowsOverlay,
							}}
							loading={comparisonLoading}
							sx={{
								height: "90%",
								width: "100%",
								zoom: 0.9,
								"& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": {
									display: "none",
								},
							}}
						/>
						<Box
							sx={{
								// gap:2,
								paddingTop: 1,
								paddingBottom: 1,
								height: "5vh",
								maxHeight: "70px",
								display: "flex",
								flexDirection: "row",
								justifyContent: "center",
								// borderWidth: '2px',
								// borderStyle: 'solid',
								// borderColor: 'red',
								// borderRadius: '8px',
							}}
						>
							<Button variant="contained" sx={{ width: "100%" }} onClick={resetRadarData}>
								Reset
							</Button>
						</Box>
					</div>
				</div>
			</div>
		);
	}
}

export { UAspotRadarChart };
